import React, { createContext, useContext, useEffect, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import { scheduleTokenExpiration, clearScheduledLogout } from './sessionManager';
import { queryClient } from '../utils/queryClient';
import { isPublicStandalonePath } from '../utils/publicStandaloneRoutes';
import { canContinuePreparedOffline, clearOfflineSession } from '../offline/offlineSession';
import { isBillingContext } from './roleUtils';
import { pushNotificationManager } from '../utils/notifications/pushManager';
import { getOfflineOwnerIdentity } from '../offline/offlineIdentity';

const AuthContext = createContext();

function isPublicAuthBypassPath() {
  return isPublicStandalonePath();
}

function decodeTokenSafely(value) {
  if (!value) return null;
  try {
    return jwtDecode(value);
  } catch {
    return null;
  }
}

function getTokenIdentity(value) {
  const decoded = decodeTokenSafely(value);
  if (!decoded?.sub) return null;
  return `${decoded.sub}:${decoded.contextType || 'legacy'}:${decoded.contextReferenceId || ''}`;
}

function clearApiResponseCache() {
  if (!window.caches?.delete) return;
  window.caches.delete('api-cache').catch(() => {});
}

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem('velaToken'));
  const [user, setUser] = useState(() => decodeTokenSafely(token));
  const [isAuthenticated, setIsAuthenticated] = useState(!!token);

  useEffect(() => {
    if (token) {
      try {
        const { exp } = jwtDecode(token);
        const now = Date.now();

        if (exp * 1000 > now) {
          const decoded = jwtDecode(token);
          setUser(decoded);
          scheduleTokenExpiration(token, handleLogout);
          localStorage.setItem('velaToken', token);
          setIsAuthenticated(true);
        } else if (!isBillingContext(decodeTokenSafely(token)) && canContinuePreparedOffline()) {
          setUser(jwtDecode(token));
          setIsAuthenticated(true);
        } else {
          handleLogout();
        }
      } catch {
        handleLogout();
      }
    } else {
      clearScheduledLogout();
      localStorage.removeItem('velaToken');
      setIsAuthenticated(false);
    }
  }, [token]);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key !== 'velaToken') return;
      if (event.oldValue === event.newValue) return;

      const previousSubject = getTokenIdentity(event.oldValue);
      const nextSubject = getTokenIdentity(event.newValue);

      // A login or context-token refresh in another tab can replace the JWT
      // without changing the account/practice identity. Adopt that token and
      // reschedule expiration instead of leaving this tab on the old timer.
      if (previousSubject && previousSubject === nextSubject && event.newValue) {
        clearScheduledLogout();
        queryClient.clear();
        clearApiResponseCache();
        const refreshedUser = decodeTokenSafely(event.newValue);
        if (isBillingContext(refreshedUser)) {
          clearOfflineSession();
          window.dispatchEvent(new CustomEvent('vela:access-context-changed'));
        }
        if (refreshedUser?.role) localStorage.setItem('userRole', refreshedUser.role.toLowerCase());
        setToken(event.newValue);
        setUser(refreshedUser);
        setIsAuthenticated(true);
        return;
      }

      clearScheduledLogout();
      queryClient.clear();
      clearApiResponseCache();
      clearOfflineSession({ logout: true, owner: getOfflineOwnerIdentity(event.oldValue) });
      pushNotificationManager.detachOnSignOut(event.oldValue);
      window.location.reload();
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const handleLogin = (newToken) => {
    const decodedUser = jwtDecode(newToken);
    if (token && decodeTokenSafely(token)?.sub !== decodedUser.sub) pushNotificationManager.detachOnSignOut(token);
    if (getTokenIdentity(token) !== getTokenIdentity(newToken) || isBillingContext(decodedUser)) {
      clearOfflineSession({ logout: true, owner: getOfflineOwnerIdentity(token) });
      window.dispatchEvent(new CustomEvent('vela:access-context-changed'));
    }
    queryClient.clear();
    clearApiResponseCache();
    setToken(newToken);
    setUser(decodedUser);
    if (decodedUser.role) localStorage.setItem('userRole', decodedUser.role.toLowerCase());
    localStorage.setItem('velaToken', newToken);
    window.dispatchEvent(new CustomEvent('vela:logged-in'));
  };
  

  const handleLogout = () => {
    pushNotificationManager.detachOnSignOut();
    clearScheduledLogout();
    clearOfflineSession({ logout: true, owner: getOfflineOwnerIdentity(token) });
    window.dispatchEvent(new CustomEvent('vela:access-context-changed'));
    localStorage.removeItem('velaToken');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userId');
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    clearApiResponseCache();
    // Cancel all active React Query subscriptions and clear cached data so
    // polling timers (e.g. useTasksQuery's 30s interval) cannot fire after
    // the token has been removed, preventing stray unauthenticated requests.
    queryClient.clear();
    if (!isPublicAuthBypassPath()) {
      window.location.href = '/login';
    }
  };

  return (
    <AuthContext.Provider value={{
      token,
      user,
      isAuthenticated,
      login: handleLogin,
      logout: handleLogout,
      handleLogout,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
